import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import {
  Clock, FolderX, Calculator, ShieldAlert, Camera, Wand2, FileCheck, Sparkles,
  Receipt, BarChart3, Package, MapPin, ChevronDown, Play, Check, Menu, X, Leaf,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { createCheckout } from '../lib/billing';
import ComingSoonModal from '../components/landing/ComingSoonModal';

const BRAND = {
  bg: '#FFFFFF',
  black: '#0A0A0A',
  text: '#0A0A0A',
  subtle: '#525966',
  mute: '#9CA3AF',
  border: '#E5E7EB',
  surfaceSoft: '#F9FAFB',
  green: '#0FC862',
  greenHi: '#14F195',
  greenGlow: 'rgba(15, 200, 98, 0.35)',
  greenSoft: 'rgba(15, 200, 98, 0.08)',
};

const PAGE_FONT = { fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif', color: BRAND.text };

const CTA_PRIMARY = {
  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '13px 22px', borderRadius: 12,
  background: BRAND.green, color: 'white', fontWeight: 700, fontSize: 15, textDecoration: 'none',
  border: 'none', cursor: 'pointer', boxShadow: `0 14px 36px -14px ${BRAND.greenGlow}, 0 1px 0 rgba(255,255,255,0.3) inset`,
  transition: 'transform 120ms ease, box-shadow 120ms ease',
};
const CTA_SECONDARY = {
  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '13px 22px', borderRadius: 12,
  background: 'white', color: BRAND.black, fontWeight: 700, fontSize: 15, textDecoration: 'none',
  border: `1.5px solid ${BRAND.border}`, cursor: 'pointer',
};

export default function Landing() {
  const { isAuthed, user } = useAuth();
  const [modal, setModal] = useState(null); // 'demo' | 'qb' | 'careers' | 'help' | null
  const [checkoutError, setCheckoutError] = useState('');
  const nav = useNavigate();

  if (isAuthed && user) {
    return <Navigate to={user.role === 'admin' ? '/admin' : '/app'} replace />;
  }

  const openDemo = () => setModal('demo');
  const openQB = () => setModal('qb');

  // Phase 23 — Pricing per-plan CTA. If logged out, send through login with a
  // redirect that reopens Settings → Billing and preselects the plan code.
  // If logged in as admin (defensive — the authed early-return above should
  // have already redirected them), route to /app with a flash toast.
  const startPlan = async (planCode) => {
    if (user?.role === 'admin') {
      setCheckoutError('Admin account — no plan required.');
      setTimeout(() => nav('/app'), 900);
      return;
    }
    if (!isAuthed) {
      nav(`/login?redirect=${encodeURIComponent(`/app/settings?billing=1&plan=${planCode}`)}`);
      return;
    }
    try {
      const r = await createCheckout(planCode);
      if (r?.url) window.location.assign(r.url);
    } catch (e) {
      setCheckoutError(e?.detail || 'Could not start checkout. Please try again.');
    }
  };

  return (
    <div data-testid="landing-page" style={{ background: BRAND.bg, minHeight: '100vh', ...PAGE_FONT }}>
      <TopNav onLogin={() => nav('/login')} onStart={() => nav('/login?signup=1')} />
      <Hero onStart={() => nav('/login?signup=1')} onDemo={openDemo} />
      <Problem />
      <HowItWorks />
      <Features />
      <MeetCleo />
      <Canadian />
      <ScoreTaxReady />
      <QuickBooks onNotify={openQB} />
      <Demo onPlay={openDemo} />
      <Pricing onStart={() => nav('/login?signup=1')} onStartPlan={startPlan} />
      <FAQ />
      <FinalCTA onStart={() => nav('/login?signup=1')} />
      <Footer />

      {checkoutError && (
        <div
          data-testid="checkout-error-toast"
          onClick={() => setCheckoutError('')}
          style={{
            position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
            padding: '12px 22px', borderRadius: 12, background: '#0A0A0A', color: 'white',
            fontSize: 14, fontWeight: 600, zIndex: 1200, cursor: 'pointer',
            boxShadow: '0 20px 60px -20px rgba(0,0,0,0.4)',
          }}
        >
          {checkoutError}
        </div>
      )}

      {modal && (
        <ComingSoonModal
          title={modal === 'qb' ? 'QuickBooks sync coming soon' : 'Product demo coming soon'}
          body={
            modal === 'qb'
              ? 'A one-click sync to QuickBooks Online is in private beta. Drop your email and we\'ll let you know when it opens up.'
              : 'The interactive product tour is launching shortly. Leave your email and we\'ll send you first access.'
          }
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

/* ------------------------------- NAV ------------------------------- */

function TopNav({ onLogin, onStart }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      data-testid="landing-nav"
      style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: scrolled ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.75)',
        backdropFilter: 'saturate(140%) blur(14px)',
        borderBottom: scrolled ? `1px solid ${BRAND.border}` : '1px solid transparent',
        transition: 'all 160ms ease',
      }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: BRAND.green, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 16, boxShadow: `0 10px 22px -10px ${BRAND.greenGlow}` }}>C</div>
          <span style={{ color: BRAND.black, fontWeight: 800, fontSize: 18, letterSpacing: '-0.02em' }}>Clasifi</span>
        </Link>
        <div className="landing-nav-links" style={{ display: 'flex', alignItems: 'center', gap: 28, fontSize: 14 }}>
          <a href="#features" style={navLink}>Features</a>
          <a href="#pricing" style={navLink}>Pricing</a>
          <a href="#faq" style={navLink}>FAQ</a>
          <button type="button" onClick={onLogin} data-testid="nav-login-btn" style={{ ...navLink, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Log in</button>
          <button type="button" onClick={onStart} data-testid="nav-start-trial-btn" style={{ ...CTA_PRIMARY, padding: '9px 16px', fontSize: 14 }}>Start Free Trial</button>
        </div>
        <button
          onClick={() => setOpen((v) => !v)} data-testid="mobile-nav-toggle"
          className="landing-mobile-only"
          style={{ display: 'none', background: 'none', border: 'none', color: BRAND.black, cursor: 'pointer' }}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div style={{ borderTop: `1px solid ${BRAND.border}`, background: 'white' }}>
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <a href="#features" style={navLink} onClick={() => setOpen(false)}>Features</a>
            <a href="#pricing" style={navLink} onClick={() => setOpen(false)}>Pricing</a>
            <a href="#faq" style={navLink} onClick={() => setOpen(false)}>FAQ</a>
            <button type="button" onClick={onLogin} style={{ ...navLink, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}>Log in</button>
            <button type="button" onClick={onStart} style={CTA_PRIMARY}>Start Free Trial</button>
          </div>
        </div>
      )}
      <style>{`
        @media (max-width: 820px) {
          .landing-nav-links { display: none !important; }
          .landing-mobile-only { display: inline-flex !important; }
        }
      `}</style>
    </nav>
  );
}
const navLink = { color: BRAND.subtle, textDecoration: 'none', fontWeight: 600 };

/* ------------------------------- HERO ------------------------------- */

function Hero({ onStart, onDemo }) {
  return (
    <section data-testid="hero-section" style={{ position: 'relative', overflow: 'hidden' }}>
      <HeroBg />
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '72px 24px 96px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 48, alignItems: 'center', position: 'relative' }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 999, background: BRAND.greenSoft, color: BRAND.green, fontWeight: 700, fontSize: 12, letterSpacing: '0.02em', marginBottom: 20 }}>
            <Sparkles size={13} /> Built for Canadian small business
          </div>
          <h1 style={{ fontSize: 'clamp(36px, 5.4vw, 60px)', lineHeight: 1.05, letterSpacing: '-0.03em', margin: 0, fontWeight: 800, color: BRAND.black }}>
            Bookkeeping that<br />actually keeps up<br />
            <span style={{ background: `linear-gradient(135deg, ${BRAND.green}, ${BRAND.greenHi})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>with you.</span>
          </h1>
          <p style={{ marginTop: 22, fontSize: 18, lineHeight: 1.55, color: BRAND.subtle, maxWidth: 520 }}>
            AI-powered receipt capture, Canadian tax rules built in, and an assistant that knows your books. Clear your backlog, stay audit-ready, keep peace of mind.
          </p>
          <div style={{ display: 'flex', gap: 12, marginTop: 32, flexWrap: 'wrap' }}>
            <button type="button" onClick={onStart} data-testid="hero-start-btn" style={CTA_PRIMARY}>Start Free Trial</button>
            <button type="button" onClick={onDemo} data-testid="hero-demo-btn" style={CTA_SECONDARY}><Play size={14} /> Watch Demo</button>
          </div>
          <div style={{ display: 'flex', gap: 24, marginTop: 28, color: BRAND.subtle, fontSize: 13 }}>
            <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><Check size={14} color={BRAND.green} /> 14-day free trial</span>
            <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><Check size={14} color={BRAND.green} /> No credit card</span>
          </div>
        </div>

        {/* Hero mockup — CSS-only glass dashboard preview */}
        <HeroMockup />
      </div>
    </section>
  );
}

function HeroBg() {
  return (
    <>
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: `radial-gradient(1200px 500px at 80% 10%, ${BRAND.greenSoft}, transparent 60%)`,
      }} />
      <div style={{
        position: 'absolute', top: 80, left: '48%', width: 420, height: 420, borderRadius: '50%',
        background: `radial-gradient(circle, ${BRAND.greenGlow} 0%, transparent 65%)`, filter: 'blur(60px)', opacity: 0.5, pointerEvents: 'none',
      }} />
    </>
  );
}

function HeroMockup() {
  return (
    <div data-testid="hero-mockup" style={{ position: 'relative' }}>
      <div style={{
        borderRadius: 20, border: `1px solid ${BRAND.border}`, background: 'white',
        boxShadow: '0 40px 80px -30px rgba(10,10,10,0.18), 0 10px 30px -10px rgba(10,10,10,0.08)',
        overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', gap: 6, padding: '12px 14px', borderBottom: `1px solid ${BRAND.border}` }}>
          <span style={dot('#F87171')} /><span style={dot('#FBBF24')} /><span style={dot('#34D399')} />
        </div>
        <div style={{ padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ fontWeight: 800, color: BRAND.black }}>This Month</div>
            <div style={{ fontSize: 12, color: BRAND.subtle }}>Jan 2026</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            <StatMini label="Receipts" value="142" delta="+18" />
            <StatMini label="GST reclaimable" value="$1,284" delta="+$214" />
          </div>
          <div style={{ padding: 12, borderRadius: 12, border: `1px solid ${BRAND.border}`, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, background: BRAND.greenSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Receipt size={20} color={BRAND.green} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>Tim Hortons</div>
              <div style={{ fontSize: 12, color: BRAND.subtle }}>Meals · GST + QST applied</div>
            </div>
            <div style={{ fontWeight: 700, color: BRAND.black }}>$18.42</div>
          </div>
          <div style={{ padding: 12, borderRadius: 12, border: `1px solid ${BRAND.border}`, display: 'flex', alignItems: 'center', gap: 12, background: BRAND.greenSoft }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Sparkles size={20} color={BRAND.green} />
            </div>
            <div style={{ flex: 1, fontSize: 13, color: BRAND.black }}><strong>Cleo:</strong> Looks like 4 Rogers receipts in a row — want to turn that into a rule?</div>
          </div>
        </div>
      </div>
      <div style={{
        position: 'absolute', right: -16, bottom: -16, width: 180, borderRadius: 16,
        border: `1px solid ${BRAND.border}`, background: 'white',
        boxShadow: '0 24px 60px -20px rgba(10,10,10,0.2)', padding: 14,
      }}>
        <div style={{ fontSize: 11, color: BRAND.subtle, fontWeight: 600 }}>Clasifi Score</div>
        <div style={{ fontSize: 34, fontWeight: 800, color: BRAND.green, letterSpacing: '-0.02em' }}>87</div>
        <div style={{ fontSize: 11, color: BRAND.subtle }}>Audit-ready</div>
      </div>
    </div>
  );
}
const dot = (c) => ({ width: 10, height: 10, borderRadius: 999, background: c, display: 'inline-block' });

function StatMini({ label, value, delta }) {
  return (
    <div style={{ padding: 12, borderRadius: 12, background: BRAND.surfaceSoft }}>
      <div style={{ fontSize: 11, color: BRAND.subtle, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: BRAND.black, letterSpacing: '-0.02em' }}>{value}</div>
      <div style={{ fontSize: 11, color: BRAND.green, fontWeight: 700 }}>{delta}</div>
    </div>
  );
}

/* ------------------------------ PROBLEM ------------------------------ */

const PROBLEMS = [
  { icon: Clock, title: 'Time-consuming bookkeeping', body: 'Hours lost every week sorting shoebox receipts and reconciling by hand.' },
  { icon: FolderX, title: 'Disorganized receipts', body: 'Paper fades. Emails get buried. Finding one receipt in April is painful.' },
  { icon: Calculator, title: 'Confusing Canadian taxes', body: 'GST, HST, QST, PST, provincial exemptions — easy to get wrong, hard to fix later.' },
  { icon: ShieldAlert, title: 'Uncertainty about records', body: 'When CRA calls, you want to know your books are tight. Most owners aren\'t sure.' },
];

function Problem() {
  return (
    <Section id="problem">
      <div style={grid4}>
        {PROBLEMS.map(({ icon: Icon, title, body }) => (
          <div key={title} style={cardStyle}>
            <div style={iconBubble}><Icon size={20} color={BRAND.green} /></div>
            <div style={{ fontWeight: 700, color: BRAND.black, marginTop: 14 }}>{title}</div>
            <div style={{ color: BRAND.subtle, marginTop: 6, fontSize: 14, lineHeight: 1.55 }}>{body}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ----------------------------- HOW IT WORKS ----------------------------- */

const STEPS = [
  { title: 'Capture', body: 'Snap a photo or forward the email. Receipt lands in Clasifi within seconds.' },
  { title: 'Organize', body: 'AI categorization + the full Canadian tax engine runs automatically.' },
  { title: 'Review & file', body: 'Approve in bulk, export QuickBooks-ready CSV, stay audit-ready year-round.' },
];

function HowItWorks() {
  return (
    <Section id="how" eyebrow="How Clasifi works" title="Three steps between a receipt and a clean set of books.">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20, position: 'relative' }}>
        {STEPS.map((s, i) => (
          <div key={s.title} style={{ ...cardStyle, position: 'relative' }}>
            <div style={{
              width: 44, height: 44, borderRadius: '50%',
              background: 'white', border: `2px solid ${BRAND.green}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: BRAND.green, fontWeight: 800, fontSize: 17,
              boxShadow: `0 10px 24px -10px ${BRAND.greenGlow}`,
            }}>{i + 1}</div>
            <div style={{ fontWeight: 700, color: BRAND.black, fontSize: 18, marginTop: 16 }}>{s.title}</div>
            <div style={{ color: BRAND.subtle, marginTop: 6, fontSize: 14, lineHeight: 1.55 }}>{s.body}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ------------------------------ FEATURES ------------------------------ */

const FEATURES = [
  { icon: Camera, title: 'Smart Receipt Capture', body: 'Photo, email, or import. The AI handles rotation, cropping, and multi-page receipts.' },
  { icon: Wand2, title: 'AI Categorization', body: 'Learns your vendors and spending patterns. Suggests categories you can accept with one tap.' },
  { icon: Package, title: 'Smart Split', body: 'One receipt, multiple categories. Clasifi allocates tax across lines automatically.' },
  { icon: FileCheck, title: 'Bookkeeper Mode', body: 'A dedicated queue that surfaces every issue — missing dates, mismatched tax, duplicates.' },
  { icon: BarChart3, title: 'Reporting', body: 'Spend by category, bucket, vendor, and period. Export CSV, PDF, or feed your accountant.' },
  { icon: Sparkles, title: 'Accountant Package', body: 'Clean QuickBooks-ready export with chart-of-account mapping and a tax summary bundle.' },
];

function Features() {
  return (
    <Section id="features">
      <div style={grid3}>
        {FEATURES.map(({ icon: Icon, title, body }) => (
          <div key={title} style={cardStyle} data-testid={`feature-${title.replace(/\s+/g, '-').toLowerCase()}`}>
            <div style={iconBubble}><Icon size={20} color={BRAND.green} /></div>
            <div style={{ fontWeight: 700, color: BRAND.black, fontSize: 17, marginTop: 14 }}>{title}</div>
            <div style={{ color: BRAND.subtle, marginTop: 6, fontSize: 14, lineHeight: 1.55 }}>{body}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ------------------------------ MEET CLEO ------------------------------ */

function MeetCleo() {
  return (
    <Section id="cleo" eyebrow="Meet Cleo" title="Your Clasifi AI assistant.">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 48, alignItems: 'center' }}>
        <div style={{ position: 'relative' }}>
          <div style={{
            padding: 28, borderRadius: 20, background: BRAND.surfaceSoft, border: `1px solid ${BRAND.border}`,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, position: 'relative',
          }}>
            <div style={{
              width: 180, height: 180, borderRadius: '50%',
              background: `radial-gradient(circle at 30% 30%, ${BRAND.greenHi}, ${BRAND.green})`,
              boxShadow: `0 30px 60px -20px ${BRAND.greenGlow}`,
              overflow: 'hidden', position: 'relative',
            }}>
              {/* Portrait image falls back silently if the asset is missing. */}
              <img src="/cleo-avatar.png" alt="Cleo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            </div>
            <div style={{
              maxWidth: 300, padding: '12px 16px', borderRadius: 16,
              background: 'white', border: `1px solid ${BRAND.border}`,
              color: BRAND.black, fontSize: 14, lineHeight: 1.5,
              boxShadow: '0 10px 24px -14px rgba(0,0,0,0.1)',
            }}>
              <strong style={{ color: BRAND.green }}>Cleo:</strong> Your Clasifi Score is 92. One receipt from Rogers is missing a date — want me to flag it for review?
            </div>
          </div>
        </div>
        <div>
          <h3 style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', marginTop: 0, color: BRAND.black }}>
            Guidance that knows <em style={{ fontStyle: 'normal', color: BRAND.green }}>your</em> books.
          </h3>
          <p style={{ color: BRAND.subtle, fontSize: 16, lineHeight: 1.65 }}>
            Cleo reads your Clasifi Score, your deadlines, and your spending patterns — then tells you what to do next. No generic advice, no spreadsheet archaeology. Ask a question, get a specific answer about your receipts.
          </p>
          <ul style={{ color: BRAND.subtle, fontSize: 15, lineHeight: 1.9, paddingLeft: 0, listStyle: 'none' }}>
            <li style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}><Check size={16} color={BRAND.green} style={{ marginTop: 4 }} /> Finds and fixes bookkeeping gaps proactively</li>
            <li style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}><Check size={16} color={BRAND.green} style={{ marginTop: 4 }} /> Tracks CRA deadlines for your province and structure</li>
            <li style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}><Check size={16} color={BRAND.green} style={{ marginTop: 4 }} /> Suggests categorization rules from your vendor history</li>
          </ul>
        </div>
      </div>
    </Section>
  );
}

/* ---------------------------- CANADIAN RULES ---------------------------- */

const PROVINCES = ['BC', 'AB', 'SK', 'MB', 'ON', 'QC', 'NB', 'NS', 'PEI', 'NL', 'YT', 'NT', 'NU'];
const TAX_PILLS = [
  { label: 'GST', value: '5%' },
  { label: 'HST', value: '13–15%' },
  { label: 'QST', value: '9.975%' },
  { label: 'PST', value: '6–7%' },
  { label: 'RST', value: '7%' },
];

function Canadian() {
  return (
    <Section id="canadian" eyebrow="Built for Canada" title={<>GST, HST, QST, PST, RST — <span style={{ color: BRAND.green }}>handled</span>.</>}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 32, alignItems: 'center' }}>
        <div>
          <p style={{ color: BRAND.subtle, fontSize: 16, lineHeight: 1.65 }}>
            Clasifi ships with the full Canadian tax engine — provincial exemptions (books in QC, kids’ clothing in BC/SK/MB/QC, bikes in BC), zero-rated groceries, exempt health services, and the CRA filing calendar for your province and business structure.
          </p>
          <p style={{ color: BRAND.subtle, fontSize: 16, lineHeight: 1.65 }}>
            Sole proprietor or incorporated? Clasifi adjusts deadlines, forms, and the questions it asks you.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 20 }}>
            {TAX_PILLS.map((p) => (
              <div key={p.label} style={{
                padding: '10px 14px', borderRadius: 12, border: `1px solid ${BRAND.border}`,
                background: 'white', fontSize: 13, fontWeight: 700, color: BRAND.black,
              }}>
                <span style={{ color: BRAND.green }}>{p.label}</span> <span style={{ color: BRAND.subtle }}>·</span> {p.value}
              </div>
            ))}
          </div>
        </div>
        <div style={{ position: 'relative', padding: 32, borderRadius: 24, background: BRAND.surfaceSoft, border: `1px solid ${BRAND.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, color: BRAND.black, fontWeight: 700 }}>
            <Leaf size={20} color={BRAND.green} /> Supported provinces
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))', gap: 8 }}>
            {PROVINCES.map((p) => (
              <div key={p} style={{
                padding: '10px 0', textAlign: 'center', borderRadius: 10,
                background: 'white', border: `1px solid ${BRAND.border}`,
                fontWeight: 700, color: BRAND.black, fontSize: 13,
              }}>{p}</div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

/* --------------------------- SCORE + TAX READY --------------------------- */

function ScoreTaxReady() {
  const score = 87;
  const r = 54; const C_ = 2 * Math.PI * r;
  const offset = C_ * (1 - score / 100);
  return (
    <Section id="score">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 48, alignItems: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{ position: 'relative', width: 220, height: 220 }}>
            <svg viewBox="0 0 140 140" width="100%" height="100%">
              <circle cx="70" cy="70" r={r} fill="none" stroke={BRAND.border} strokeWidth="10" />
              <circle
                cx="70" cy="70" r={r} fill="none"
                stroke={BRAND.green} strokeWidth="10" strokeLinecap="round"
                strokeDasharray={C_} strokeDashoffset={offset}
                transform="rotate(-90 70 70)"
              />
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ fontSize: 48, fontWeight: 800, color: BRAND.black, letterSpacing: '-0.03em' }}>{score}</div>
              <div style={{ fontSize: 12, color: BRAND.subtle, fontWeight: 600 }}>Clasifi Score</div>
            </div>
          </div>
        </div>
        <div>
          <h3 style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', marginTop: 0, color: BRAND.black }}>Proactive, not reactive.</h3>
          <p style={{ color: BRAND.subtle, fontSize: 16, lineHeight: 1.65 }}>
            Your Clasifi Score tracks every bookkeeping issue — mismatched tax, missing fields, duplicates, uncategorized spend. Fix things as they happen, not when it’s time to file.
          </p>
          <p style={{ color: BRAND.subtle, fontSize: 16, lineHeight: 1.65 }}>
            The Tax Ready view tells you exactly what’s needed before your next deadline, by category and bucket.
          </p>
        </div>
      </div>
    </Section>
  );
}

/* ------------------------------ QUICKBOOKS ------------------------------ */

function QuickBooks({ onNotify }) {
  return (
    <Section id="quickbooks">
      <div style={{
        padding: 32, borderRadius: 20, border: `1px solid ${BRAND.border}`, background: 'white',
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24, alignItems: 'center',
      }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 999, background: BRAND.greenSoft, color: BRAND.green, fontWeight: 700, fontSize: 12, marginBottom: 12 }}>
            Coming Soon
          </div>
          <h3 style={{ margin: 0, fontSize: 22, color: BRAND.black, fontWeight: 800, letterSpacing: '-0.02em' }}>
            Seamless chart-of-account mapping.
          </h3>
          <p style={{ color: BRAND.subtle, marginTop: 10, fontSize: 15, lineHeight: 1.6 }}>
            Every Clasifi category maps to a QuickBooks account. One-click sync pushes approved receipts straight into QBO — no re-keying, no CSV gymnastics.
          </p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onNotify} data-testid="qb-notify-btn" style={CTA_PRIMARY}>Notify me</button>
        </div>
      </div>
    </Section>
  );
}

/* -------------------------------- DEMO -------------------------------- */

const DEMO_SLIDES = [
  { label: 'Receipt list', body: 'Smart queue — issues bubble to the top.' },
  { label: 'Cleo chat', body: 'Ask about your spend. Get specific answers.' },
  { label: 'Tax Ready', body: 'Province-aware, deadline-aware, structure-aware.' },
];

function Demo({ onPlay }) {
  const [idx, setIdx] = useState(0);
  return (
    <Section id="demo">
      <div style={{ position: 'relative' }}>
        <div style={{
          borderRadius: 24, border: `1px solid ${BRAND.border}`,
          background: 'linear-gradient(180deg, #0F1115 0%, #14181F 100%)',
          color: 'white', padding: 32, minHeight: 320, position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', inset: 0,
            background: `radial-gradient(600px 300px at 50% 30%, ${BRAND.greenGlow}, transparent 70%)`,
            pointerEvents: 'none',
          }} />
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 20 }}>
            <button type="button" onClick={onPlay} aria-label="Play demo" data-testid="demo-play-btn" style={{
              width: 74, height: 74, borderRadius: '50%',
              background: `linear-gradient(135deg, ${BRAND.green}, ${BRAND.greenHi})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', border: 'none', cursor: 'pointer',
              boxShadow: `0 20px 60px -10px ${BRAND.greenGlow}`,
            }}><Play size={28} fill="white" /></button>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{DEMO_SLIDES[idx].label}</div>
            <div style={{ fontSize: 14, color: '#A1A7B3', maxWidth: 360, textAlign: 'center' }}>
              {DEMO_SLIDES[idx].body}
            </div>
          </div>
        </div>
        <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'center' }}>
          {DEMO_SLIDES.map((_, i) => (
            <button key={i} data-testid={`demo-dot-${i}`} aria-label={`Slide ${i + 1}`} onClick={() => setIdx(i)} style={{
              width: idx === i ? 24 : 8, height: 8, borderRadius: 999,
              background: idx === i ? BRAND.green : BRAND.border,
              border: 'none', cursor: 'pointer', transition: 'all 150ms ease',
            }} />
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ------------------------------ PRICING ------------------------------ */

const PRICING = [
  {
    key: 'starter',
    name: 'Starter', price: 19, blurb: 'For solo operators getting organized.',
    features: ['50 receipts / month', 'OCR + GST / HST tax rules', '1 user', 'Email support'],
    cta: 'Start 14-day free trial',
  },
  {
    key: 'business',
    name: 'Business', price: 49, blurb: 'For growing Canadian small businesses.',
    highlight: true,
    features: ['500 receipts / month', 'Smart Split', 'Bookkeeper Mode', 'Cleo AI', 'Full Canadian tax engine', '3 users', 'Priority email'],
    cta: 'Start 14-day free trial',
  },
  {
    key: 'accountant',
    name: 'Accountant', price: 99, blurb: 'For firms managing multiple clients.',
    features: ['2,500 receipts / month', 'Multi-client', 'White-label export (QB-ready CSV)', 'QuickBooks integration (soon)', 'Priority support'],
    cta: 'Start 14-day free trial',
  },
];

const COMPARE = [
  ['OCR receipt capture', '✓', '✓', '✓'],
  ['Full Canadian tax engine', '—', '✓', '✓'],
  ['Smart Split + Bookkeeper', '—', '✓', '✓'],
  ['Cleo AI', '—', '✓', '✓'],
  ['Multi-client / White-label', '—', '—', '✓'],
  ['Priority support', '—', '—', '✓'],
];

function Pricing({ onStart, onStartPlan }) {
  return (
    <Section id="pricing">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
        {PRICING.map((p) => (
          <div
            key={p.key}
            data-testid={`pricing-${p.key}`}
            style={{
              position: 'relative',
              borderRadius: 20, padding: 28,
              background: 'white',
              border: `${p.highlight ? 2 : 1}px solid ${p.highlight ? BRAND.green : BRAND.border}`,
              boxShadow: p.highlight ? `0 30px 60px -20px ${BRAND.greenGlow}` : '0 8px 24px -12px rgba(10,10,10,0.06)',
              transform: p.highlight ? 'translateY(-4px)' : 'none',
            }}
          >
            {p.highlight && (
              <div style={{
                position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                padding: '5px 12px', borderRadius: 999,
                background: `linear-gradient(135deg, ${BRAND.green}, ${BRAND.greenHi})`,
                color: 'white', fontWeight: 700, fontSize: 11, letterSpacing: '0.04em',
                boxShadow: `0 10px 24px -10px ${BRAND.greenGlow}`,
              }}>MOST POPULAR</div>
            )}
            <div style={{ color: BRAND.subtle, fontWeight: 700, fontSize: 13 }}>{p.name}</div>
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 44, fontWeight: 800, letterSpacing: '-0.03em', color: BRAND.black }}>${p.price}</span>
              <span style={{ color: BRAND.subtle, fontSize: 14 }}>CAD / mo</span>
            </div>
            <div style={{ color: BRAND.subtle, fontSize: 13, marginTop: 4 }}>{p.blurb}</div>
            <ul style={{ paddingLeft: 0, listStyle: 'none', marginTop: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {p.features.map((f) => (
                <li key={f} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', color: BRAND.text, fontSize: 14 }}>
                  <Check size={16} color={BRAND.green} style={{ marginTop: 3 }} />{f}
                </li>
              ))}
            </ul>
            <button type="button" onClick={() => (onStartPlan ? onStartPlan(p.key) : onStart())} data-testid={`pricing-cta-${p.key}`} style={{
              ...CTA_PRIMARY, width: '100%', marginTop: 22, justifyContent: 'center',
              background: p.highlight ? BRAND.green : BRAND.black, boxShadow: p.highlight ? `0 14px 36px -14px ${BRAND.greenGlow}` : 'none',
            }}>{p.cta}</button>
            <div style={{ textAlign: 'center', fontSize: 12, color: BRAND.subtle, marginTop: 10 }}>
              14-day free trial — no credit card required
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 36, overflowX: 'auto', border: `1px solid ${BRAND.border}`, borderRadius: 16, background: 'white' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: BRAND.surfaceSoft, textAlign: 'left' }}>
              <th style={th}>Feature</th>
              <th style={{ ...th, textAlign: 'center' }}>Starter</th>
              <th style={{ ...th, textAlign: 'center', color: BRAND.green }}>Business</th>
              <th style={{ ...th, textAlign: 'center' }}>Accountant</th>
            </tr>
          </thead>
          <tbody>
            {COMPARE.map((row) => (
              <tr key={row[0]} style={{ borderTop: `1px solid ${BRAND.border}` }}>
                <td style={td}>{row[0]}</td>
                <td style={{ ...td, textAlign: 'center' }}>{row[1]}</td>
                <td style={{ ...td, textAlign: 'center', color: BRAND.green, fontWeight: 700 }}>{row[2]}</td>
                <td style={{ ...td, textAlign: 'center' }}>{row[3]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}
const th = { padding: '14px 18px', color: BRAND.subtle, fontWeight: 700, fontSize: 13 };
const td = { padding: '14px 18px', color: BRAND.text };

/* -------------------------------- FAQ -------------------------------- */

const FAQS = [
  { q: 'Do I need bookkeeping experience to use Clasifi?', a: 'Nope. If you can take a photo of a receipt or forward an email, you can run Clasifi. The AI handles categorization and the Canadian tax engine. Your accountant will love the exports.' },
  { q: 'Does Clasifi handle Canadian tax rules?', a: 'Yes — GST, HST, QST, PST, and RST, with provincial exemptions baked in (books in QC, children’s clothing in BC/SK/MB/QC, basic groceries, prescription drugs, residential rent, and more). CRA filing deadlines are tracked for your province and structure.' },
  { q: 'Will my accountant be able to work with Clasifi?', a: 'Yes. Clasifi exports QuickBooks-ready CSV with your chart of accounts mapped. The Accountant plan supports multi-client workspaces and white-label exports.' },
  { q: 'How is my data kept secure?', a: 'Receipts are stored encrypted at rest, tokens are HTTPS-only, and we never sell your data. Enterprise-grade controls (SSO, SAML, audit logs) are on the roadmap.' },
  { q: 'Can I cancel or change my subscription at any time?', a: 'Yes. Upgrades and downgrades are prorated. Cancel from Settings — no phone call, no penalty.' },
  { q: 'What support is available?', a: 'All plans include email support with real humans. Business + Accountant get priority queues and (soon) scheduled calls.' },
];

function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <Section id="faq">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {FAQS.map((f, i) => {
          const isOpen = open === i;
          return (
            <button key={f.q} type="button" onClick={() => setOpen(isOpen ? -1 : i)} data-testid={`faq-item-${i}`} style={{
              textAlign: 'left', background: 'white', border: `1px solid ${BRAND.border}`,
              borderRadius: 14, padding: 20, cursor: 'pointer', color: BRAND.text,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20 }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>{f.q}</span>
                <ChevronDown size={18} color={BRAND.subtle} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }} />
              </div>
              {isOpen && (
                <div style={{ marginTop: 10, color: BRAND.subtle, fontSize: 14, lineHeight: 1.6 }}>{f.a}</div>
              )}
            </button>
          );
        })}
      </div>
    </Section>
  );
}

/* ----------------------------- FINAL CTA ----------------------------- */

function FinalCTA({ onStart }) {
  return (
    <section data-testid="final-cta" style={{ padding: '0 24px' }}>
      <div style={{
        maxWidth: 1200, margin: '48px auto 72px', padding: '56px 32px',
        borderRadius: 28, background: `linear-gradient(135deg, ${BRAND.black} 0%, #1a1f29 100%)`,
        color: 'white', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', inset: 0, background: `radial-gradient(600px 300px at 90% 20%, ${BRAND.greenGlow}, transparent 70%)`,
          pointerEvents: 'none',
        }} />
        <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24, alignItems: 'center' }}>
          <div>
            <p style={{ color: '#F3F4F6', fontSize: 18, margin: 0, maxWidth: 560, lineHeight: 1.5, fontWeight: 500 }}>
              Snap, forward, upload — Clasifi handles the rest. Start your 14-day free trial. No credit card required.
            </p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onStart} data-testid="final-cta-btn" style={{ ...CTA_PRIMARY, padding: '15px 26px', fontSize: 16 }}>Start Free Trial</button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------- FOOTER ------------------------------- */

const FOOTER = [
  { title: 'Product', items: [
    { label: 'Features', href: '#features' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'Cleo AI', href: '#cleo' },
    { label: 'Changelog', href: '#', coming: true },
  ]},
  { title: 'Company', items: [
    { label: 'About', href: '#', coming: true },
    { label: 'Contact', href: 'mailto:support@clasifi.ca' },
    { label: 'Careers', href: '#', coming: true },
  ]},
  { title: 'Legal', items: [
    { label: 'Terms', href: '#', coming: true },
    { label: 'Privacy', href: '#', coming: true },
    { label: 'Security', href: '#', coming: true },
  ]},
  { title: 'Help', items: [
    { label: 'Help Center', href: '/app/support' },
    { label: 'Status', href: '#', coming: true },
    { label: 'Contact support', href: 'mailto:support@clasifi.ca' },
  ]},
];

function Footer() {
  const [modal, setModal] = useState(false);
  return (
    <footer data-testid="landing-footer" style={{ borderTop: `1px solid ${BRAND.border}`, background: 'white' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '48px 24px 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 32 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: BRAND.green, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14 }}>C</div>
              <span style={{ fontWeight: 800, color: BRAND.black, fontSize: 16 }}>Clasifi</span>
            </div>
            <p style={{ color: BRAND.subtle, fontSize: 13, marginTop: 12, maxWidth: 240, lineHeight: 1.6 }}>
              Modern bookkeeping for Canadian small businesses. Built in Canada.
            </p>
          </div>
          {FOOTER.map((col) => (
            <div key={col.title}>
              <div style={{ fontSize: 12, fontWeight: 700, color: BRAND.subtle, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{col.title}</div>
              <ul style={{ listStyle: 'none', padding: 0, marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {col.items.map((it) => (
                  <li key={it.label}>
                    {it.coming ? (
                      <button onClick={() => setModal(true)} data-testid={`footer-${it.label.toLowerCase().replace(/\s+/g,'-')}`} type="button" style={{ background: 'none', border: 'none', padding: 0, color: BRAND.text, cursor: 'pointer', fontSize: 14 }}>
                        {it.label}
                      </button>
                    ) : it.href.startsWith('#') ? (
                      <a href={it.href} style={{ color: BRAND.text, textDecoration: 'none', fontSize: 14 }}>{it.label}</a>
                    ) : (
                      <Link to={it.href} style={{ color: BRAND.text, textDecoration: 'none', fontSize: 14 }}>{it.label}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 36, paddingTop: 20, borderTop: `1px solid ${BRAND.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ color: BRAND.subtle, fontSize: 13 }}>© 2026 Clasifi Inc. · Made in Canada <span aria-hidden>🍁</span></div>
          <div style={{ display: 'flex', gap: 16, color: BRAND.subtle, fontSize: 13 }}>
            <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><MapPin size={12} /> Vancouver, BC</span>
          </div>
        </div>
      </div>
      {modal && <ComingSoonModal title="Coming soon" body="This page is being built. Drop your email and we'll let you know." onClose={() => setModal(false)} />}
    </footer>
  );
}

/* ----------------------------- PRIMITIVES ----------------------------- */

function Section({ id, eyebrow, title, children }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } });
    }, { threshold: 0.1 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <section
      id={id} data-testid={`section-${id}`}
      ref={ref}
      style={{
        padding: '72px 24px', opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(16px)',
        transition: 'opacity 500ms ease, transform 500ms ease',
      }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {eyebrow && (
          <div style={{ color: BRAND.green, fontWeight: 700, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 }}>{eyebrow}</div>
        )}
        {title && (
          <h2 style={{ fontSize: 'clamp(26px, 3.4vw, 40px)', fontWeight: 800, letterSpacing: '-0.02em', margin: 0, color: BRAND.black, maxWidth: 820 }}>
            {title}
          </h2>
        )}
        <div style={{ marginTop: (title || eyebrow) ? 36 : 0 }}>{children}</div>
      </div>
    </section>
  );
}

const cardStyle = {
  padding: 24, borderRadius: 18, border: `1px solid ${BRAND.border}`, background: 'white',
  transition: 'transform 150ms ease, box-shadow 150ms ease',
};
const iconBubble = {
  width: 42, height: 42, borderRadius: 12, background: BRAND.greenSoft,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const grid3 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20 };
const grid4 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20 };
